import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type { FirewallRulesetIssue } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import { isOneOf } from '../lib/isOneOf.ts';
import {
  FIREWALL_DENY_AS,
  FIREWALL_RULE_ACTIONS,
  FIREWALL_SYNTHESIZE_AS,
  appendRuleDraft,
  moveRuleDraft,
  removeRuleDraft,
  updateRuleDraft,
  validateRuleDrafts,
  type FirewallRuleDraft,
} from '../lib/firewallRuleDraft.ts';
import { Button, FormField, IconButton, Input, Select, Textarea } from '../ui/index.ts';

/**
 * The rule chain editor (#1760).
 *
 * Priority IS the array order — evaluation stops at the first match — so
 * reordering is the highest-consequence edit on this surface. It is done with
 * up/down buttons over LOCAL draft state and nothing else. There is no
 * "reorder the live version" call, and no drag library: the whole chain is
 * submitted as a new draft version, which is what keeps a mis-drop from being
 * a production incident and what makes every reorder land in version history
 * as a reviewable, revertable document.
 *
 * `description` is required here even though #1757 keeps it out of the rule
 * hash. That exclusion is deliberate downstream — a comment change must not
 * mint a new hash — and it is exactly why the requirement has to live in the
 * UI: nothing further down the pipe will ever refuse an undescribed rule, and
 * the description is the only thing that explains a refusal to whoever reads
 * the interception record months later.
 */

export function FirewallRuleListEditor({
  drafts,
  serverIssues,
  disabled,
  idPrefix,
  onChange,
}: {
  readonly drafts: readonly FirewallRuleDraft[];
  /** Issues returned by the last rejected write, rendered against their rules. */
  readonly serverIssues: readonly FirewallRulesetIssue[];
  readonly disabled: boolean;
  readonly idPrefix: string;
  readonly onChange: (next: readonly FirewallRuleDraft[]) => void;
}): JSX.Element {
  const t = useT();
  const localIssues = validateRuleDrafts(drafts);

  /**
   * The message to hang under one field, from either source.
   *
   * Matched on the engine's `at` path, which is why the local pre-flight emits
   * `at` in the engine's spelling. Issues the engine reports without one (or
   * against the ruleset rather than a rule) are not dropped — the caller
   * renders the whole list through `FirewallIssueList` as well.
   */
  const allIssues: readonly FirewallRulesetIssue[] = [...localIssues, ...serverIssues];
  function issueFor(index: number, field: string): string | undefined {
    return allIssues.find((issue) => issue.at === `rules[${String(index)}].${field}`)?.message;
  }

  return (
    <div className="flex flex-col gap-3" data-testid={`${idPrefix}-firewall-rules`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-label text-paper-low">{t('firewall_editor.order_hint')}</p>
        <Button
          variant="secondary"
          onClick={() => onChange(appendRuleDraft(drafts))}
          disabled={disabled}
          data-testid={`${idPrefix}-firewall-rule-add`}
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          <span>{t('firewall_editor.add_rule')}</span>
        </Button>
      </div>

      {drafts.length === 0 ? (
        <p
          className="m-0 rounded-lg bg-well p-3 text-label text-paper-mid"
          data-testid={`${idPrefix}-firewall-rules-empty`}
        >
          {t('firewall_editor.empty')}
        </p>
      ) : null}

      {drafts.map((draft, index) => {
        const fieldId = `${idPrefix}-firewall-rule-${draft.key}`;
        return (
          <div
            key={draft.key}
            className="flex flex-col gap-3 rounded-lg border border-line-soft bg-card p-3"
            data-testid={`${idPrefix}-firewall-rule-row-${String(index)}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="inline-flex items-center gap-2">
                <span className="rounded bg-raised px-2 py-0.5 font-mono text-micro text-paper-mid">
                  {t('firewall_editor.position', { position: index + 1 })}
                </span>
                <span className="text-label text-paper-low">
                  {t('firewall_editor.position_hint')}
                </span>
              </span>
              <span className="inline-flex items-center gap-1">
                <IconButton
                  size="sm"
                  label={t('firewall_editor.move_up')}
                  onClick={() => onChange(moveRuleDraft(drafts, draft.key, 'up'))}
                  disabled={disabled || index === 0}
                  data-testid={`${fieldId}-up`}
                >
                  <ArrowUp aria-hidden="true" className="h-4 w-4" />
                </IconButton>
                <IconButton
                  size="sm"
                  label={t('firewall_editor.move_down')}
                  onClick={() => onChange(moveRuleDraft(drafts, draft.key, 'down'))}
                  disabled={disabled || index === drafts.length - 1}
                  data-testid={`${fieldId}-down`}
                >
                  <ArrowDown aria-hidden="true" className="h-4 w-4" />
                </IconButton>
                <IconButton
                  size="sm"
                  label={t('firewall_editor.remove_rule')}
                  onClick={() => onChange(removeRuleDraft(drafts, draft.key))}
                  disabled={disabled}
                  className="hover:bg-bad-dim hover:text-bad"
                  data-testid={`${fieldId}-remove`}
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                </IconButton>
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                label={t('firewall_editor.rule_id')}
                fieldId={`${fieldId}-id`}
                required
                error={issueFor(index, 'id')}
              >
                <Input
                  id={`${fieldId}-id`}
                  value={draft.id}
                  mono
                  invalid={issueFor(index, 'id') !== undefined}
                  onChange={(event) =>
                    onChange(updateRuleDraft(drafts, draft.key, { id: event.target.value }))}
                  disabled={disabled}
                  data-testid={`${fieldId}-id`}
                />
              </FormField>
              <FormField
                label={t('firewall_editor.rule_action')}
                fieldId={`${fieldId}-action`}
                hint={t('firewall_editor.rule_action_hint')}
              >
                <Select
                  id={`${fieldId}-action`}
                  value={draft.action}
                  onValueChange={(next) => {
                    if (isOneOf(next, FIREWALL_RULE_ACTIONS)) {
                      onChange(updateRuleDraft(drafts, draft.key, { action: next }));
                    }
                  }}
                  disabled={disabled}
                  mono
                  testId={`${fieldId}-action`}
                  options={FIREWALL_RULE_ACTIONS.map((action) => ({
                    value: action,
                    label: action,
                  }))}
                />
              </FormField>
            </div>

            <FormField
              label={t('firewall_editor.rule_description')}
              fieldId={`${fieldId}-description`}
              required
              error={issueFor(index, 'description')}
              hint={t('firewall_editor.rule_description_hint')}
            >
              <Input
                id={`${fieldId}-description`}
                value={draft.description}
                invalid={issueFor(index, 'description') !== undefined}
                onChange={(event) =>
                  onChange(updateRuleDraft(drafts, draft.key, {
                    description: event.target.value,
                  }))}
                disabled={disabled}
                data-testid={`${fieldId}-description`}
              />
            </FormField>

            {draft.action === 'deny' ? (
              <FormField
                label={t('firewall_editor.rule_deny_as')}
                fieldId={`${fieldId}-deny-as`}
                hint={t('firewall_editor.rule_deny_as_hint')}
                error={issueFor(index, 'deny')}
              >
                <Select
                  id={`${fieldId}-deny-as`}
                  value={draft.denyAs}
                  onValueChange={(next) => {
                    if (isOneOf(next, FIREWALL_DENY_AS)) {
                      onChange(updateRuleDraft(drafts, draft.key, { denyAs: next }));
                    }
                  }}
                  disabled={disabled}
                  mono
                  testId={`${fieldId}-deny-as`}
                  options={FIREWALL_DENY_AS.map((as) => ({ value: as, label: as }))}
                />
              </FormField>
            ) : null}

            {draft.action === 'synthesize' ? (
              <FormField
                label={t('firewall_editor.rule_synthesize_as')}
                fieldId={`${fieldId}-synthesize-as`}
                hint={t('firewall_editor.rule_synthesize_as_hint')}
                error={issueFor(index, 'synthesize')}
              >
                <Select
                  id={`${fieldId}-synthesize-as`}
                  value={draft.synthesizeAs}
                  onValueChange={(next) => {
                    if (isOneOf(next, FIREWALL_SYNTHESIZE_AS)) {
                      onChange(updateRuleDraft(drafts, draft.key, { synthesizeAs: next }));
                    }
                  }}
                  disabled={disabled}
                  mono
                  testId={`${fieldId}-synthesize-as`}
                  options={FIREWALL_SYNTHESIZE_AS.map((as) => ({ value: as, label: as }))}
                />
              </FormField>
            ) : null}

            <FormField
              label={t('firewall_editor.rule_when')}
              fieldId={`${fieldId}-when`}
              required
              error={issueFor(index, 'when')}
              hint={t('firewall_editor.rule_when_hint')}
            >
              <Textarea
                id={`${fieldId}-when`}
                value={draft.whenText}
                rows={6}
                mono
                invalid={issueFor(index, 'when') !== undefined}
                onChange={(event) =>
                  onChange(updateRuleDraft(drafts, draft.key, { whenText: event.target.value }))}
                disabled={disabled}
                data-testid={`${fieldId}-when`}
              />
            </FormField>
          </div>
        );
      })}
    </div>
  );
}
